/**
 * PreK Assessment Constants
 * Centralized constants for all PreK assessment and practice screens
 */

import { TRACK_RANGES } from '../../types/constants';
import { QUESTION_SCREEN_AUDIO } from '../../hooks/usePreKAudio';

// ============================================================================
// Font Styles for Numerals
// ============================================================================
// Re-export from design system for backward compatibility
export { NUMERAL_FONT_STYLE } from '../../styles/design-system/typography';

// ============================================================================
// Track IDs
// ============================================================================
export type PreKTrackId =
  | 'TRACK_PREK_NAMING'
  | 'TRACK_PREK_SUBITIZING'
  | 'TRACK_PREK_COUNTING'
  | 'TRACK_PREK_WHATCOMESNEXT';

export const PREK_TRACKS = {
  NAMING: 'TRACK_PREK_NAMING' as const,
  SUBITIZING: 'TRACK_PREK_SUBITIZING' as const,
  COUNTING: 'TRACK_PREK_COUNTING' as const,
  WHATCOMESNEXT: 'TRACK_PREK_WHATCOMESNEXT' as const,
};

// ============================================================================
// Assessment Configuration
// ============================================================================
// Fixed number of test questions per assessment (replaces 60-second wall clock)
export const TOTAL_ASSESSMENT_QUESTIONS = 10;

// ============================================================================
// Timer Constants (in milliseconds)
// ============================================================================
export const TIMER_CONSTANTS = {
  // Wall clock limit for all assessments (legacy - no longer used for completion)
  WALL_CLOCK_LIMIT_MS: 60000, // 60 seconds

  // Question time limits by track (generous — assessment tests accuracy, not speed)
  QUESTION_TIME_LIMITS: {
    [PREK_TRACKS.NAMING]: 20000,        // 20 seconds
    [PREK_TRACKS.SUBITIZING]: 20000,    // 20 seconds
    [PREK_TRACKS.COUNTING]: 20000,      // 20 seconds
    [PREK_TRACKS.WHATCOMESNEXT]: 20000,  // 20 seconds
  } as const,

  // Timer tick interval
  TICK_INTERVAL_MS: 100,

  // Transition/feedback delays
  CORRECT_ANSWER_DELAY_MS: 300,
  WRONG_ANSWER_FLASH_MS: 1000,
  QUESTION_READY_DELAY_MS: 500,

  // Subitizing flash duration (1 second for PreK - longer for young children)
  SUBITIZING_FLASH_MS: 1000,
  SUBITIZING_BLANK_MS: 100,
} as const;

// ============================================================================
// Fluency Targets (in seconds)
// ============================================================================
export const FLUENCY_TARGETS_SEC = {
  [PREK_TRACKS.NAMING]: 6,
  [PREK_TRACKS.SUBITIZING]: 6,
  [PREK_TRACKS.COUNTING]: 4,
  [PREK_TRACKS.WHATCOMESNEXT]: 4,
} as const;

// ============================================================================
// Warmup Configuration
// ============================================================================
export const WARMUP_CONFIG = {
  // Fixed number of warmup questions before transitioning to timed assessment
  MAX_WARMUP_QUESTIONS: 3,

  // TTS frequency during warmup (play on first question only)
  WARMUP_TTS_INTERVAL: 1, // Every N questions (1 = every question)

  // TTS frequency during test (play every 3rd question)
  TEST_TTS_INTERVAL: 3,
} as const;

// ============================================================================
// Fact ID Mappings
// ============================================================================

/**
 * Get the starting fact ID for a track
 */
export const getTrackFactStart = (trackId: PreKTrackId): number => {
  const range = TRACK_RANGES[trackId];
  return range ? range[0] : 0;
};

/**
 * Convert a digit/quantity/target to a fact ID for a given track
 */
export const valueToFactId = (trackId: PreKTrackId, value: number): string => {
  const startFact = getTrackFactStart(trackId);

  switch (trackId) {
    case PREK_TRACKS.NAMING:
      // Digits 0-9 map to FACT1574-FACT1583
      return `FACT${startFact + value}`;

    case PREK_TRACKS.SUBITIZING:
      // Quantities 1-4 map to FACT1584-FACT1587
      return `FACT${startFact - 1 + value}`;

    case PREK_TRACKS.COUNTING:
      // Quantities 1-5 map to FACT1588-FACT1592
      return `FACT${startFact - 1 + value}`;

    case PREK_TRACKS.WHATCOMESNEXT:
      // Targets 3-9 map to FACT1595-FACT1601
      // FACT1593=target1, FACT1594=target2 (unused), FACT1595=target3, etc.
      return `FACT${startFact + (value - 1)}`;

    default:
      return `FACT${startFact + value}`;
  }
};

/**
 * Convert a fact ID to its value (digit/quantity/target) for a given track
 */
export const factIdToValue = (trackId: PreKTrackId, factId: string): number => {
  const factNumber = parseInt(factId.replace('FACT', ''));
  const startFact = getTrackFactStart(trackId);

  switch (trackId) {
    case PREK_TRACKS.NAMING:
      return factNumber - startFact;

    case PREK_TRACKS.SUBITIZING:
      return factNumber - startFact + 1;

    case PREK_TRACKS.COUNTING:
      return factNumber - startFact + 1;

    case PREK_TRACKS.WHATCOMESNEXT:
      // FACT1595 = target 3, etc.
      return factNumber - startFact + 1;

    default:
      return factNumber - startFact;
  }
};

// ============================================================================
// Answer Choice Generation
// ============================================================================
export const ANSWER_CHOICE_CONFIG = {
  // Number of answer choices to display
  CHOICE_COUNT: 4,

  // Range for random choices by track
  CHOICE_RANGES: {
    [PREK_TRACKS.NAMING]: { min: 0, max: 9 },
    [PREK_TRACKS.SUBITIZING]: { min: 0, max: 5 },
    [PREK_TRACKS.COUNTING]: { min: 0, max: 6 },
    [PREK_TRACKS.WHATCOMESNEXT]: { min: 0, max: 10 },
  } as const,
} as const;

/**
 * Generate answer choices for a target value
 */
export const generateAnswerChoices = (
  trackId: PreKTrackId,
  target: number,
  useNearMisses: boolean = false
): number[] => {
  const choices = [target];
  const used = new Set([target]);
  const range = ANSWER_CHOICE_CONFIG.CHOICE_RANGES[trackId];

  if (useNearMisses) {
    // Add near-misses (±1) first
    const nearMissPlus = target + 1;
    const nearMissMinus = target - 1;

    if (nearMissPlus >= range.min && nearMissPlus <= range.max && !used.has(nearMissPlus)) {
      choices.push(nearMissPlus);
      used.add(nearMissPlus);
    }
    if (nearMissMinus >= range.min && nearMissMinus <= range.max && !used.has(nearMissMinus)) {
      choices.push(nearMissMinus);
      used.add(nearMissMinus);
    }

    // Add one far miss (±2)
    const farMissOptions = [target + 2, target - 2].filter(
      opt => opt >= range.min && opt <= range.max && !used.has(opt)
    );
    if (farMissOptions.length > 0) {
      const randomFarMiss = farMissOptions[Math.floor(Math.random() * farMissOptions.length)];
      choices.push(randomFarMiss);
      used.add(randomFarMiss);
    }
  }

  // Fill remaining slots with random valid numbers
  while (choices.length < ANSWER_CHOICE_CONFIG.CHOICE_COUNT) {
    const candidate = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    if (!used.has(candidate)) {
      choices.push(candidate);
      used.add(candidate);
    }
  }

  // Shuffle the choices
  return choices.sort(() => Math.random() - 0.5);
};

// ============================================================================
// Audio Configuration
// ============================================================================
export const getInstructionAudio = (trackId: PreKTrackId) => {
  switch (trackId) {
    case PREK_TRACKS.SUBITIZING:
      return QUESTION_SCREEN_AUDIO.subitizing.instruction;
    case PREK_TRACKS.COUNTING:
      return QUESTION_SCREEN_AUDIO.counting.instruction;
    case PREK_TRACKS.WHATCOMESNEXT:
      return QUESTION_SCREEN_AUDIO.whatComesNext.instruction;
    default:
      return null; // Number naming uses number-specific audio
  }
};

export const getNumberAudio = (digit: number) => {
  return QUESTION_SCREEN_AUDIO.tapNumber[digit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber];
};

// ============================================================================
// Visual Configuration
// ============================================================================
export const VISUAL_CONFIG = {
  // Grid positions for dot patterns (subitizing)
  SUBITIZING_GRID: [
    { x: 10, y: 10 }, { x: 50, y: 10 }, { x: 90, y: 10 },
    { x: 10, y: 50 }, { x: 50, y: 50 }, { x: 90, y: 50 },
    { x: 10, y: 90 }, { x: 50, y: 90 }, { x: 90, y: 90 },
  ],

  // Grid positions for counting (4x8 grid)
  COUNTING_GRID: [
    // Row 1
    { x: 12.5, y: 6.25 }, { x: 37.5, y: 6.25 }, { x: 62.5, y: 6.25 }, { x: 87.5, y: 6.25 },
    // Row 2
    { x: 12.5, y: 18.75 }, { x: 37.5, y: 18.75 }, { x: 62.5, y: 18.75 }, { x: 87.5, y: 18.75 },
    // Row 3
    { x: 12.5, y: 31.25 }, { x: 37.5, y: 31.25 }, { x: 62.5, y: 31.25 }, { x: 87.5, y: 31.25 },
    // Row 4
    { x: 12.5, y: 43.75 }, { x: 37.5, y: 43.75 }, { x: 62.5, y: 43.75 }, { x: 87.5, y: 43.75 },
    // Row 5
    { x: 12.5, y: 56.25 }, { x: 37.5, y: 56.25 }, { x: 62.5, y: 56.25 }, { x: 87.5, y: 56.25 },
    // Row 6
    { x: 12.5, y: 68.75 }, { x: 37.5, y: 68.75 }, { x: 62.5, y: 68.75 }, { x: 87.5, y: 68.75 },
    // Row 7
    { x: 12.5, y: 81.25 }, { x: 37.5, y: 81.25 }, { x: 62.5, y: 81.25 }, { x: 87.5, y: 81.25 },
    // Row 8
    { x: 12.5, y: 93.75 }, { x: 37.5, y: 93.75 }, { x: 62.5, y: 93.75 }, { x: 87.5, y: 93.75 },
  ],
} as const;

// ============================================================================
// Quantity/Target Generation
// ============================================================================
export const VALUE_RANGES = {
  [PREK_TRACKS.NAMING]: { min: 0, max: 9 },
  [PREK_TRACKS.SUBITIZING]: { min: 1, max: 4 },
  [PREK_TRACKS.COUNTING]: { min: 1, max: 5 },
  [PREK_TRACKS.WHATCOMESNEXT]: { min: 3, max: 9 },
} as const;

/**
 * Generate a random value for a track, optionally avoiding a specific value
 */
export const generateRandomValue = (trackId: PreKTrackId, avoid?: number): number => {
  const range = VALUE_RANGES[trackId];
  let value: number;

  // Special weighting for counting (favor 4s and 5s)
  if (trackId === PREK_TRACKS.COUNTING) {
    const weightedOptions = [1, 2, 3, 4, 4, 5, 5, 5];
    do {
      value = weightedOptions[Math.floor(Math.random() * weightedOptions.length)];
    } while (avoid !== undefined && value === avoid);
    return value;
  }

  // Standard random generation for other tracks
  do {
    value = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
  } while (avoid !== undefined && value === avoid);

  return value;
};

// ============================================================================
// Assessment Question Sequence Generation
// ============================================================================

/**
 * Generate a fixed sequence of question values for assessment.
 * Ensures each fact appears at least once, fills remaining slots with
 * balanced repeats, and avoids consecutive duplicates.
 */
export const generateAssessmentSequence = (trackId: PreKTrackId): number[] => {
  const range = VALUE_RANGES[trackId];
  const allValues: number[] = [];
  for (let i = range.min; i <= range.max; i++) {
    allValues.push(i);
  }

  const sequence: number[] = [];

  while (sequence.length < TOTAL_ASSESSMENT_QUESTIONS) {
    // Shuffle all values for this round
    const shuffled = [...allValues].sort(() => Math.random() - 0.5);

    // Avoid consecutive duplicate at the boundary
    if (sequence.length > 0 && shuffled[0] === sequence[sequence.length - 1] && shuffled.length > 1) {
      const swapIndex = 1 + Math.floor(Math.random() * (shuffled.length - 1));
      [shuffled[0], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[0]];
    }

    for (const value of shuffled) {
      if (sequence.length >= TOTAL_ASSESSMENT_QUESTIONS) break;
      sequence.push(value);
    }
  }

  return sequence;
};
